import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Briefcase, HandHeart, ShieldCheck, Check } from "lucide-react";
import { Header } from "../sections/Header";
import { Footer } from "../sections/Footer";
import Seo from "../components/Seo";
import { BonsaiTree } from "../components/BonsaiTree";
import { ImpactIntakeForms } from "../components/ImpactIntakeForm";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** First Veteran Spotlight — live progress toward sponsoring veteran #1. */
const SpotlightTracker = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!API_BASE) return;
    fetch(`${API_BASE}/api/impact/spotlight`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <div className="mt-6 w-full max-w-xl" data-testid="impact-spotlight">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          Launch progress
        </span>
        <span className="font-mono text-[11px] tabular-nums" style={{ color: "#39FF14" }} data-testid="impact-spotlight-pct">
          {data.done}/{data.total} · {data.progress_pct}%
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: "rgba(255,255,255,0.07)" }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.max(2, data.progress_pct)}%`, background: "linear-gradient(90deg,#39FF14,#00E5FF)" }}
        />
      </div>
      <div className="space-y-2">
        {data.milestones.map((m) => (
          <div key={m.key} className="flex items-start gap-2.5" style={{ opacity: m.done ? 1 : 0.5 }} data-testid={`impact-spotlight-${m.key}`}>
            <span
              className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full border shrink-0"
              style={m.done
                ? { borderColor: "rgba(57,255,20,0.6)", background: "rgba(57,255,20,0.14)" }
                : { borderColor: "rgba(255,255,255,0.16)", background: "transparent" }}
            >
              {m.done && <Check className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={3} />}
            </span>
            <div>
              <div className="text-[13px] text-white leading-tight">{m.title}</div>
              <div className="text-[11px] text-zinc-500 leading-tight">{m.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PILLARS = [
  {
    icon: Wrench,
    title: "Train",
    copy: "Fund and connect veterans into EV, battery, and clean-energy technician training with partner programs.",
  },
  {
    icon: Briefcase,
    title: "Employ",
    copy: "Match trained veterans with employer partners hiring across the EV economy — service, energy, software, fleet.",
  },
  {
    icon: HandHeart,
    title: "Plant",
    copy: "Every milestone plants trees in a veteran's name through the EVIQ Earth Initiative forest.",
  },
];

export default function ImpactPage() {
  const scrollToForms = () => document.getElementById("impact-forms")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div className="min-h-screen bg-black text-white" data-testid="impact-page">
      <Seo
        title="Veteran Green Initiative — EVIQ Impact"
        description="They powered our mission. Now we'll help power theirs. EVIQ trains, employs, and plants trees in honor of veterans entering the EV economy."
        canonical="https://eviq.pro/impact"
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 overflow-hidden" style={{ background: "linear-gradient(180deg,#030503,#06121a)" }}>
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] mb-5" style={{ color: "#00E5FF" }}>
              <ShieldCheck className="w-3.5 h-3.5" /> EVIQ Veteran Green Initiative
            </div>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              They powered our mission. <span style={{ color: "#00E5FF" }}>Now we'll help power theirs.</span>
            </h1>
            <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-2xl mt-6">
              Veterans kept the lights on for all of us. EVIQ's initiative gives them a direct path into the
              fastest-growing corner of the economy — electric vehicles and clean energy — while planting a
              forest in their honor.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <button
                onClick={scrollToForms}
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-mono text-[12px] uppercase tracking-wider font-bold transition-transform hover:scale-[1.03]"
                style={{ background: "#00E5FF", color: "#001114" }}
                data-testid="impact-help-build-btn"
              >
                Help Build the Mission <ArrowRight className="w-4 h-4" />
              </button>
              <span
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-mono text-[12px] uppercase tracking-wider border cursor-default"
                style={{ borderColor: "rgba(255,255,255,0.14)", color: "#71717a" }}
                data-testid="impact-sponsor-coming-soon"
              >
                Sponsor a Veteran — Coming Soon
              </span>
            </div>
            <p className="font-mono text-[10px] text-zinc-600 leading-relaxed max-w-xl mt-5">
              EVIQ is not currently operating a charitable fund and contributions are not tax-deductible.
              We're gathering interested supporters and partners while the program's legal and accounting
              structure is established.
            </p>
          </div>
        </section>

        {/* First mission */}
        <section className="py-14 border-y" style={{ borderColor: "rgba(0,229,255,0.15)", background: "rgba(0,229,255,0.03)" }}>
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="flex gap-1 shrink-0">
              {Array.from({ length: 5 }).map((_, i) => (
                <BonsaiTree key={i} size={26} color="#00E5FF" style={{ opacity: 0.5 + 0.1 * i }} />
              ))}
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-1" style={{ color: "#00E5FF" }}>
                First mission
              </div>
              <div className="text-white text-xl sm:text-2xl font-semibold" data-testid="impact-first-mission">
                Sponsor EVIQ's first veteran.
              </div>
              <div className="text-zinc-400 text-sm mt-1 max-w-xl">
                One veteran. One training pathway. One job. One grove of trees planted in their name. Then we scale.
              </div>
              <SpotlightTracker />
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-4">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-2xl border p-6" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
                <p.icon className="w-6 h-6 mb-3" style={{ color: "#00E5FF" }} strokeWidth={1.8} />
                <div className="text-white font-semibold text-lg">{p.title}</div>
                <div className="text-zinc-400 text-sm leading-relaxed mt-1">{p.copy}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Intake forms */}
        <section id="impact-forms" className="py-16 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] mb-2" style={{ color: "#39FF14" }}>
              Get involved · live today
            </div>
            <h2 className="text-white font-semibold text-2xl sm:text-3xl mb-8">
              Raise your hand — veteran, trainer, employer, or brand.
            </h2>
            <ImpactIntakeForms defaultType="veteran" />
          </div>
        </section>

        {/* Partner seal teaser */}
        <section className="py-16 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center gap-8">
            <img src="/impact-partner-seal.png" alt="Verified EVIQ Impact Partner seal" className="w-36 h-36 object-contain shrink-0 rounded-full" />
            <div>
              <div className="text-white font-semibold text-xl flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" style={{ color: "#00E5FF" }} /> Verified EVIQ Impact Partner
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed mt-2 max-w-lg">
                Corporate partners who complete the verification standards will carry the Verified EVIQ Impact
                Partner seal. Available to approved participating EVIQ Impact Partners.
              </p>
              <Link to="/partners" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider mt-3" style={{ color: "#00E5FF" }} data-testid="impact-partners-link">
                Corporate partnership page <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
